'use client';

import { useEffect, useState } from 'react';
import { prepareInlineCanvasSvg } from '@/lib/prepareInlineCanvasSvg';
import styles from './ViewingCanvas.module.css';

export type CanvasSvgBackgroundProps = {
  url: string;
  /** When true (default), clip to the canvas page box. */
  clipToCanvas: boolean;
  canvasWidth: number;
  canvasHeight: number;
};

/**
 * Canvas SVG background for containd.space.
 * Clipped: page-boxed `<img>` inside overflow:hidden (host ClipPath parity).
 * Unclipped: inline SVG with overflow:visible so shapes can bleed into the letterbox.
 */
export function CanvasSvgBackground({
  url,
  clipToCanvas,
  canvasWidth,
  canvasHeight,
}: CanvasSvgBackgroundProps) {
  const [inlineSvg, setInlineSvg] = useState<string | null>(null);

  useEffect(() => {
    if (clipToCanvas) {
      setInlineSvg(null);
      return;
    }
    let cancelled = false;
    setInlineSvg(null);
    void (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const text = await res.text();
        if (cancelled) return;
        const prepared = prepareInlineCanvasSvg(text, canvasWidth, canvasHeight);
        if (prepared) setInlineSvg(prepared);
      } catch {
        // Fall back to <img> below when fetch/parse fails.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, clipToCanvas, canvasWidth, canvasHeight]);

  if (!clipToCanvas && inlineSvg) {
    return (
      <div
        className={styles.canvasSvgBgUnclipped}
        data-canvas-svg-bg="1"
        data-clip-svg="0"
        // Owner-published SVG media; scripts stripped in prepareInlineCanvasSvg.
        dangerouslySetInnerHTML={{ __html: inlineSvg }}
      />
    );
  }

  return (
    <div
      className={
        clipToCanvas ? styles.canvasSvgBgClip : styles.canvasSvgBgUnclipped
      }
      data-canvas-svg-bg="1"
      data-clip-svg={clipToCanvas ? '1' : '0'}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        className={styles.canvasSvgBgImg}
        src={url}
        alt=""
        draggable={false}
      />
    </div>
  );
}
